import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { UserProfile, UserRole } from '../lib/database.types';

interface AuthContextValue {
    session: Session | null;
    user: User | null;
    profile: UserProfile | null;
    role: UserRole | null;
    loading: boolean;
    // True while the user is in the middle of a password recovery flow.
    // App.tsx routes them to ResetPasswordPage when this is set, regardless
    // of session state, until they submit a new password.
    passwordRecovery: boolean;
    signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
    signOut: () => Promise<void>;
    sendPasswordResetEmail: (email: string) => Promise<{ error: Error | null }>;
    updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [passwordRecovery, setPasswordRecovery] = useState(false);
    const queryClient = useQueryClient();

    // Load initial session
    useEffect(() => {
        let mounted = true;

        supabase.auth.getSession().then(({ data }) => {
            if (!mounted) return;
            setSession(data.session);
            if (!data.session) setLoading(false);
        });

        const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
            if (!mounted) return;

            // Password recovery: user clicked the reset email link. Supabase
            // creates a temporary session and fires this event. The app must
            // route them to the set-new-password UI before any other view.
            if (event === 'PASSWORD_RECOVERY') {
                setPasswordRecovery(true);
            }

            setSession(newSession);
            if (!newSession) {
                setProfile(null);
                setLoading(false);
            }
        });

        return () => {
            mounted = false;
            authListener.subscription.unsubscribe();
        };
    }, []);

    // Load profile when session changes
    useEffect(() => {
        if (!session?.user?.id) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();

            if (cancelled) return;
            if (error) {
                // Profile row may not exist yet for a freshly-signed-up user.
                // In production, profile creation is handled by a database
                // trigger on auth.users INSERT, or by an admin manually.
                // Until we get an admin-created profile, show viewer-only state.
                console.error('Failed to load user_profiles row:', error.message);
                setProfile(null);
            } else {
                setProfile(data as UserProfile);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [session?.user?.id]);

    const signIn = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
    };

    const signOut = async () => {
        // Clear all cached server data first so the next user (if someone else
        // signs in on this browser) does not see a flash of the previous
        // user's cached queries while new ones are refetching.
        queryClient.clear();
        await supabase.auth.signOut();
        setPasswordRecovery(false);
    };

    const sendPasswordResetEmail = async (email: string) => {
        // redirectTo defaults to the Site URL configured in Supabase Auth.
        // We rely on the Supabase client's detectSessionInUrl behavior to
        // pick up the access_token from the URL hash on the landing page
        // and fire the PASSWORD_RECOVERY event.
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        return { error };
    };

    const updatePassword = async (newPassword: string) => {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (!error) {
            // Password updated successfully. Clear the recovery flag so the
            // normal app flow resumes. The session is already authenticated.
            setPasswordRecovery(false);
        }
        return { error };
    };

    const value: AuthContextValue = {
        session,
        user: session?.user ?? null,
        profile,
        role: profile?.role ?? null,
        loading,
        passwordRecovery,
        signIn,
        signOut,
        sendPasswordResetEmail,
        updatePassword,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}

/** True if the current user has at least the given role. Role hierarchy: admin > field > viewer. */
export function useHasRole(minimum: UserRole): boolean {
    const { role } = useAuth();
    if (!role) return false;
    if (minimum === 'viewer') return true; // all authenticated roles include viewer permissions
    if (minimum === 'field') return role === 'field' || role === 'admin';
    return role === 'admin';
}
